// src/components/Footer/Footer.jsx (ou ton chemin actuel)
import React, { useEffect, useMemo, useState } from "react";
import { FaFacebookF, FaInstagram } from "react-icons/fa";
import { FiMail, FiPhone, FiMapPin, FiExternalLink } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { getSettings } from "../../services/settingsServices";

function cn(...c) {
  return c.filter(Boolean).join(" ");
}

// (optionnel) rend /uploads/... absolu vers l’API
const toAbsolute = (u) => {
  if (!u) return "";
  const s = String(u).trim();
  if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:"))
    return s;
  if (!s.startsWith("/uploads")) return s;
  const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
  return `${base}${s}`;
};

export default function Footer() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    nom_club: "",
    logo_club: "",
    adresse_club: "",
    tel_club: "",
    mail_club: "",
    facebook_club: "",
    instagram_club: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const data = await getSettings();
        if (Array.isArray(data) && data.length > 0) setSettings(data[0]);
      } catch (e) {
        console.error("Erreur settings footer:", e);
      }
    })();
  }, []);

  const mapUrl = useMemo(() => {
    const addr = (settings.adresse_club || "").trim();
    if (!addr) return "";
    const encoded = encodeURIComponent(addr);
    return `https://www.google.com/maps?q=${encoded}&output=embed`;
  }, [settings.adresse_club]);

  const mapLink = useMemo(() => {
    const addr = (settings.adresse_club || "").trim();
    if (!addr) return "";
    const encoded = encodeURIComponent(addr);
    return `https://www.google.com/maps?q=${encoded}`;
  }, [settings.adresse_club]);

  // Scroll to anchor (si tu veux l’utiliser dans des liens du footer)
  const handleNavigate = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    else navigate("/", { state: { scrollTo: id } });
  };

  const logoSrc = toAbsolute(settings.logo_club);

  return (
    <footer className="relative overflow-hidden">
      {/* fond + accents */}
      <div
        aria-hidden
        className={cn(
          "absolute inset-0",
          "bg-slate-950",
          "dark:bg-black"
        )}
      />
      <div
        aria-hidden
        className={cn(
          "absolute inset-x-0 top-0 h-px",
          "bg-gradient-to-r from-transparent via-primary/70 to-transparent"
        )}
      />
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full",
          "bg-primary/15 blur-3xl"
        )}
      />
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute -bottom-28 -left-24 h-72 w-72 rounded-full",
          "bg-primary/10 blur-3xl"
        )}
      />

      <div className="relative mx-auto max-w-7xl px-4 py-10 sm:py-12">
        <div className="grid gap-8 lg:gap-10 lg:grid-cols-12 items-start">
          {/* Col 1 : Map + Contact */}
          <div className="lg:col-span-5">
            <div
              className={cn(
                "rounded-3xl overflow-hidden",
                "border border-slate-800/70",
                "bg-white/5 backdrop-blur",
                "shadow-[0_10px_30px_rgba(0,0,0,.25)]"
              )}
            >
              {/* Map */}
              <div className="relative">
                {mapUrl ? (
                  <iframe
                    title="Carte du club"
                    src={mapUrl}
                    width="100%"
                    height="220"
                    className="block w-full"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                ) : (
                  <div className="h-[220px] grid place-items-center text-sm text-slate-300">
                    Adresse non renseignée
                  </div>
                )}

                {mapLink ? (
                  <a
                    href={mapLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      "absolute right-4 top-4",
                      "inline-flex items-center gap-2",
                      "rounded-full px-3 py-2 text-xs font-bold",
                      "bg-slate-950/70 text-white",
                      "border border-slate-700/70",
                      "hover:border-primary/70 hover:text-primary transition"
                    )}
                    title="Ouvrir dans Google Maps"
                  >
                    <FiExternalLink />
                    Ouvrir
                  </a>
                ) : null}
              </div>

              {/* Infos contact */}
              <div className="p-5 sm:p-6">
                <h3 className="text-sm font-extrabold tracking-wider text-slate-200 uppercase">
                  Contact
                </h3>

                {settings.adresse_club?.trim() ? (
                  <div className="mt-3 flex items-start gap-3 text-slate-200">
                    <span className="mt-0.5 text-primary">
                      <FiMapPin />
                    </span>
                    <div className="text-sm leading-relaxed">
                      <div className="font-semibold">Adresse</div>
                      <div className="text-slate-300">{settings.adresse_club}</div>
                    </div>
                  </div>
                ) : null}

                <div className="mt-4 flex flex-wrap gap-2">
                  {settings.mail_club?.trim() ? (
                    <a
                      href={`mailto:${settings.mail_club}`}
                      className={cn(
                        "inline-flex items-center gap-2",
                        "rounded-full px-3 py-2 text-xs font-bold",
                        "bg-white/10 text-slate-100",
                        "border border-slate-800/70",
                        "hover:border-primary/70 hover:text-primary transition"
                      )}
                      title="Envoyer un email"
                    >
                      <FiMail className="text-primary" />
                      {settings.mail_club}
                    </a>
                  ) : null}

                  {settings.tel_club?.trim() ? (
                    <a
                      href={`tel:${settings.tel_club}`}
                      className={cn(
                        "inline-flex items-center gap-2",
                        "rounded-full px-3 py-2 text-xs font-bold",
                        "bg-white/10 text-slate-100",
                        "border border-slate-800/70",
                        "hover:border-primary/70 hover:text-primary transition"
                      )}
                      title="Appeler"
                    >
                      <FiPhone className="text-primary" />
                      {settings.tel_club}
                    </a>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          {/* Col 2 : Logo + nom + réseaux */}
          <div className="lg:col-span-3">
            <div
              className={cn(
                "rounded-3xl",
                "border border-slate-800/70",
                "bg-white/5 backdrop-blur",
                "shadow-[0_10px_30px_rgba(0,0,0,.25)]",
                "p-6"
              )}
            >
              <div className="flex flex-col items-center text-center">
                {logoSrc ? (
                  <img
                    src={logoSrc}
                    alt={`Logo ${settings.nom_club || "Club"}`}
                    className="h-28 w-auto drop-shadow-[0_12px_22px_rgba(0,0,0,.45)]"
                    loading="lazy"
                  />
                ) : (
                  <div className="h-28 w-28 rounded-2xl bg-white/10 border border-slate-800/60" />
                )}

                <h2 className="mt-5 text-xl font-extrabold tracking-tight text-slate-50">
                  {settings.nom_club || "Votre club"}
                </h2>

                {/* soulignement premium */}
                <div
                  aria-hidden
                  className="mt-3 h-[3px] w-16 rounded-full bg-gradient-to-r from-transparent via-primary to-transparent opacity-90"
                />

                {/* Réseaux */}
                <div className="mt-5 flex items-center gap-3">
                  {settings.facebook_club?.trim() ? (
                    <a
                      href={settings.facebook_club}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cn(
                        "h-11 w-11 rounded-full grid place-items-center",
                        "bg-white/10 border border-slate-800/70",
                        "text-slate-100",
                        "hover:border-primary/70 hover:text-primary hover:-translate-y-[1px]",
                        "transition"
                      )}
                      title="Facebook"
                      aria-label="Facebook"
                    >
                      <FaFacebookF />
                    </a>
                  ) : null}

                  {settings.instagram_club?.trim() ? (
                    <a
                      href={settings.instagram_club}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={cn(
                        "h-11 w-11 rounded-full grid place-items-center",
                        "bg-white/10 border border-slate-800/70",
                        "text-slate-100",
                        "hover:border-primary/70 hover:text-primary hover:-translate-y-[1px]",
                        "transition"
                      )}
                      title="Instagram"
                      aria-label="Instagram"
                    >
                      <FaInstagram />
                    </a>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          {/* Col 3 : Liens */}
          <div className="lg:col-span-4">
            <div
              className={cn(
                "rounded-3xl",
                "border border-slate-800/70",
                "bg-white/5 backdrop-blur",
                "shadow-[0_10px_30px_rgba(0,0,0,.25)]",
                "p-6"
              )}
            >
              <h3 className="text-sm font-extrabold tracking-wider text-slate-200 uppercase">
                Liens utiles
              </h3>

              <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                {[
                  ["Règlement intérieur", "/reglement-interieur"],
                  ["Mentions légales", "/mentions-legales"],
                  ["Confidentialité", "/politique-confidentialite"],
                  ["Conditions", "/conditions-utilisation"],
                  ["FAQ", "/faq"],
                  ["Tutoriel", "/tutoriel"],
                  ["Documents", "/documents-obligatoires"],
                ].map(([label, href]) => (
                  <a
                    key={href}
                    href={href}
                    className={cn(
                      "text-slate-300 hover:text-primary transition",
                      "underline-offset-4 hover:underline"
                    )}
                  >
                    {label}
                  </a>
                ))}
              </div>

              {/* Exemple d’ancres (si tu veux scroller vers sections home) */}
              <div className="mt-6">
                <h4 className="text-xs font-bold text-slate-200 uppercase tracking-wider">
                  Navigation rapide
                </h4>
                <div className="mt-3 flex flex-wrap gap-2">
                  {[
                    ["Disciplines", "cours"],
                    ["Équipes", "nosEquipes"],
                    ["Groupes", "groupes"],
                    ["Contact", "contact"],
                  ].map(([label, id]) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => handleNavigate(id)}
                      className={cn(
                        "rounded-full px-3 py-2 text-xs font-bold",
                        "bg-white/10 text-slate-100",
                        "border border-slate-800/70",
                        "hover:border-primary/70 hover:text-primary transition"
                      )}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div
          className={cn(
            "mt-10 pt-6",
            "border-t border-slate-800/70",
            "text-center text-xs sm:text-sm text-slate-400"
          )}
        >
          <p>
            © {new Date().getFullYear()} — Tous droits réservés —{" "}
            <a
              href="https://www.monappliclub.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-200 hover:text-primary transition underline-offset-4 hover:underline"
            >
              MonAppliClub
            </a>{" "}
            —{" "}
            <a
              href="https://profile.vkard.io/8f8e4d95-cf28-40b0-afc6-2ec677889d49/ab099714aa"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-200 hover:text-primary transition underline-offset-4 hover:underline"
            >
              Laurent VICTORIN
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
